import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { zarzadyZlewni, type ZarzadZlewniDto, type ZarzadStatsResponse } from '@/api/endpoints';
import { Icon } from '@/components/Icon';

export function ZarzadyZlewniList() {
  const [list, setList] = useState<ZarzadZlewniDto[] | null>(null);
  const [stats, setStats] = useState<ZarzadStatsResponse | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    zarzadyZlewni.list().then(setList).catch(e => setErr(String(e)));
    zarzadyZlewni.stats().then(setStats).catch(() => { /* nonfatal */ });
  }, []);

  const filtered = useMemo(() => {
    if (!list) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter(z =>
      z.name.toLowerCase().includes(q) ||
      z.rzgwName.toLowerCase().includes(q) ||
      (z.addressCity ?? '').toLowerCase().includes(q));
  }, [list, filter]);

  // Group by RZGW
  const grouped = useMemo(() => {
    const out: Record<string, ZarzadZlewniDto[]> = {};
    for (const z of filtered) {
      if (!out[z.rzgwName]) out[z.rzgwName] = [];
      out[z.rzgwName].push(z);
    }
    return out;
  }, [filtered]);

  if (err) return <div className="text-red-500 p-4">Błąd: {err}</div>;
  if (!list) return <div className="text-ink-400 p-4">Ładowanie…</div>;

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Zarządy Zlewni</h1>
          <div className="text-ink-400 text-[13px]">
            Słownik wszystkich 50 Zarządów Zlewni Wód Polskich pogrupowany po 11 RZGW.
            Adresy, kontakty i statystyki opracowań/odrzuceń per ZZ.
          </div>
        </div>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filtr (nazwa, RZGW, miasto)…"
          className="input input-sm w-72"
        />
      </header>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Top ZZ — najwięcej odrzuceń</div></div>
            <div className="card-body !p-0">
              {stats.topRejectingZz.length === 0 && (
                <div className="text-ink-400 text-[12px] p-4">Brak odrzuceń w bazie.</div>
              )}
              {stats.topRejectingZz.length > 0 && (
                <table className="table-hw">
                  <tbody>
                    {stats.topRejectingZz.slice(0, 10).map((r, i) => (
                      <tr key={r.id}>
                        <td className="text-[11px] text-ink-400 font-mono w-8">#{i + 1}</td>
                        <td>
                          <NavLink to={`/admin/zarzady-zlewni/${r.id}`} className="text-water-700 hover:underline">
                            {r.name}
                          </NavLink>
                          {r.secondary && <div className="text-[11px] text-ink-500">{r.secondary}</div>}
                        </td>
                        <td className="text-right font-mono font-semibold text-amber-700">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><div className="card-title">Top opracowania — najczęściej wracające</div></div>
            <div className="card-body !p-0">
              {stats.mostRejectedStudies.length === 0 && (
                <div className="text-ink-400 text-[12px] p-4">Brak odrzuceń w bazie.</div>
              )}
              {stats.mostRejectedStudies.length > 0 && (
                <table className="table-hw">
                  <tbody>
                    {stats.mostRejectedStudies.slice(0, 10).map((r, i) => (
                      <tr key={r.id}>
                        <td className="text-[11px] text-ink-400 font-mono w-8">#{i + 1}</td>
                        <td>
                          <NavLink to={`/studies/${r.id}`} className="text-water-700 hover:underline">
                            {r.name}
                          </NavLink>
                          {r.secondary && <div className="text-[11px] text-ink-500">Ujęcie: {r.secondary}</div>}
                        </td>
                        <td className="text-right font-mono font-semibold text-amber-700">{r.count}×</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {Object.keys(grouped).sort().map(rzgw => (
        <div key={rzgw} className="card mb-3">
          <div className="card-head">
            <div className="card-title">{rzgw}</div>
            <span className="ml-2 text-[11px] text-ink-400">({grouped[rzgw].length} ZZ)</span>
          </div>
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Zarząd Zlewni</th>
                  <th>Adres</th>
                  <th>Kontakt</th>
                  <th className="text-right">Ujęcia</th>
                  <th className="text-right">Opracowania</th>
                  <th className="text-right">Odrzucenia</th>
                </tr>
              </thead>
              <tbody>
                {grouped[rzgw].map(z => (
                  <tr key={z.id} className="cursor-pointer hover:bg-ink-50"
                      onClick={() => window.location.assign(`/admin/zarzady-zlewni/${z.id}`)}>
                    <td>
                      <div className="font-semibold text-ink-900">{z.name}</div>
                      <div className="text-[10px] text-ink-400 font-mono">{z.code}</div>
                    </td>
                    <td className="text-[12px]">
                      {z.addressLine1 ? (
                        <>
                          <div>{z.addressLine1}</div>
                          <div className="text-ink-500">{z.addressPostalCode} {z.addressCity}</div>
                        </>
                      ) : <span className="text-ink-400 italic">brak adresu</span>}
                    </td>
                    <td className="text-[11px]">
                      {z.phone && <div className="font-mono">{z.phone}</div>}
                      {z.email && <div className="text-water-700 truncate max-w-[200px]" title={z.email}>{z.email}</div>}
                    </td>
                    <td className="text-right font-mono tabular-nums">{z.intakesCount ?? 0}</td>
                    <td className="text-right font-mono tabular-nums">{z.studiesCount ?? 0}</td>
                    <td className="text-right font-mono tabular-nums">
                      {(z.rejectionsCount ?? 0) > 0
                        ? <span className="text-amber-700 font-semibold">{z.rejectionsCount}</span>
                        : <span className="text-ink-400">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
